import { FC, useEffect, useState } from 'react';
import { VoidFn } from 'types/baseTypes';
import CustomModal from 'components/custom-modal/CustomModal';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import CustomTextArea from 'components/custom-text-area/CustomTextArea';
import RadioWithLabel from 'components/radio-with-label/RadioWithLabel';
import { CircleCloseIcon, OutlinedInfoIcon } from 'assets/icons';
import { InvoiceByIdV2, InvoiceStatus } from 'containers/invoices/invoices.model';
import { getInvoiceStatus, getInvoiceStatusBackgroundColor, getInvoiceStatusColor } from 'containers/invoices/utils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { CLOSE_INVOICE_REASON_LABEL, CloseInvoiceReason } from 'containers/invoices/constants';
import { useCloseInvoiceMutation } from 'containers/invoices/api';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import If from 'components/If/If';
import { NotifierTypes } from 'constants/notifierConstants';
import { showNotification } from 'utils/Notifier';
import { APIErrorStatuses } from 'services/type';

interface CloseInvoiceModalProps {
  isOpen: boolean;
  onClose: VoidFn;
  invoice: InvoiceByIdV2;
  onConfirm?: VoidFn;
}

const CloseInvoiceModal: FC<CloseInvoiceModalProps> = ({ isOpen, onClose, onConfirm, invoice }) => {
  const [selectedReason, setSelectedReason] = useState<CloseInvoiceReason>(CloseInvoiceReason.AMOUNT_ADJUSTED);
  const [note, setNote] = useState('');
  const currentTeam = useAppSelector(selectCurrentTeam);
  const dispatch = useAppDispatch();
  const [closeInvoice, { isLoading, isError, error: closingError }] = useCloseInvoiceMutation();

  useEffect(() => {
    if (isError && closingError) {
      const errorData = closingError?.['data'];
      const errorCode = errorData?.code;
      const errorMessage = errorData?.details;

      if (closingError?.['status'] === APIErrorStatuses.STATUS_409 || errorCode === APIErrorStatuses.STATUS_409) {
        showNotification(dispatch, NotifierTypes.ERROR, errorMessage);
      } else {
        showNotification(dispatch, NotifierTypes.ERROR, 'Failed to close invoice');
      }
    }
  }, [isError, closingError, dispatch]);

  const handleConfirm = async () => {
    try {
      await closeInvoice({
        teamId: currentTeam.id.toString(),
        invoiceId: invoice.invoiceId,
        closureReason: selectedReason,
        closureAmount: invoice.pendingAmount || 0,
        closureNote: note
      }).unwrap();
      showNotification(dispatch, NotifierTypes.SUCCESS, 'Invoice closed successfully');
      onConfirm?.();
      handleClose();
    } catch (err) {
      // Error is already handled by RTK Query mutation state (isError)
    }
  };

  const handleClose = () => {
    setNote('');
    setSelectedReason(CloseInvoiceReason.AMOUNT_ADJUSTED);
    onClose();
  };

  const handleNoteChange = (value: string) => {
    setNote(value);
  };

  const invoiceStatus = invoice?.status as InvoiceStatus;
  const statusText = getInvoiceStatus(invoiceStatus);
  const statusColor = getInvoiceStatusColor(invoiceStatus);
  const statusBgColor = getInvoiceStatusBackgroundColor(invoiceStatus);
  const pendingAmount = invoice?.pendingAmount || 0;

  return (
    <CustomModal open={isOpen} closeModal={handleClose} widthInPx="525px" enableOutsideClick={false}>
      <div className="flex flex-col p-6">
        <div className="mb-1 flex items-center justify-between">
          <div className="text-xl font-bold text-headingGray">Close invoice</div>
          <CircleCloseIcon onClick={handleClose} className="shrink-0 cursor-pointer" />
        </div>

        <div className="mb-9 flex items-center gap-1">
          <div className="text-sbase font-medium text-headingGray">{invoice?.invoiceNumber || ''}</div>
          <div className="text-xs text-[#D9D9D9]">•</div>
          <div
            className="rounded-md px-2.5 py-0.5 text-px13 font-normal"
            style={{
              backgroundColor: statusBgColor,
              color: statusColor
            }}>
            {statusText}
          </div>
        </div>

        <div className="mb-7 flex items-center justify-between">
          <div className="text-17px font-semibold text-headingGray">Remaining invoice balance</div>
          <div className="text-17px font-semibold text-error">{getAmountWithCurrency(pendingAmount)}</div>
        </div>
        <div className="mb-4 border-b border-borderGray" />

        <div className="mb-5">
          <div className="mb-4 text-sbase font-semibold text-headingGray">Reason for closure</div>
          <div className="space-y-4">
            <RadioWithLabel
              isSelected={selectedReason === CloseInvoiceReason.AMOUNT_ADJUSTED}
              label={`Close as ${CLOSE_INVOICE_REASON_LABEL?.[CloseInvoiceReason.AMOUNT_ADJUSTED]?.toLowerCase()}`}
              onClick={() => setSelectedReason(CloseInvoiceReason.AMOUNT_ADJUSTED)}
              containerClassname="text-sbase"
            />
            <RadioWithLabel
              isSelected={selectedReason === CloseInvoiceReason.BAD_DEBT}
              label={`Close as ${CLOSE_INVOICE_REASON_LABEL?.[CloseInvoiceReason.BAD_DEBT]?.toLowerCase()}`}
              onClick={() => setSelectedReason(CloseInvoiceReason.BAD_DEBT)}
              containerClassname="text-sbase"
            />
          </div>
        </div>

        <div className="mb-6">
          <div className="text-sbase font-semibold text-headingGray">Note</div>
          <div className="mb-1 text-px13 font-semibold text-accentText">Visible to customer</div>
          <CustomTextArea
            id="close-invoice-note"
            name="close-invoice-note"
            value={note}
            onChange={handleNoteChange}
            placeholder="Add note here"
            className="min-h-[85px] border border-borderGray outline-none"
            resizeTextArea={true}
            maxHeight={120}
            maxLength={80}
            showRemainingChar={false}
          />
          <If condition={invoice?.hasManualPayment}>
            <div className="mt-2 flex items-start gap-1 text-px13 text-accentText">
              <OutlinedInfoIcon className="path path-fill-current mt-1 h-3 w-3 shrink-0" />
              <span>Once this invoice is closed, any manually collected payments linked to it cannot be deleted.</span>
            </div>
          </If>
        </div>

        <div className="flex justify-end gap-3">
          <CustomButton
            id="close-invoice-cancel-btn"
            type={ButtonType.SECONDARY}
            onClick={handleClose}
            className="h-10 w-[115px]"
            childrenContainerStyles="text-17px font-semibold"
            disabled={isLoading}>
            Cancel
          </CustomButton>
          <CustomButton
            id="close-invoice-confirm-btn"
            type={ButtonType.PRIMARY}
            onClick={handleConfirm}
            className="h-10 w-[130px]"
            childrenContainerStyles="text-17px font-semibold"
            isLoading={isLoading}>
            Close invoice
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default CloseInvoiceModal;
